import React, { useCallback, useMemo, useRef } from 'react';
import _, { isNil, remove } from 'lodash';

import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { Button, InputAdornment, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import { DragHandle, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import indexToLetter from '@dyl/utils/indexToLetter';
import Draggable from 'react-draggable';
import useSize from '@dyl/hooks/useSize';

import Node from './Node';
import { deriveDisplayOrder, useMicroCheck } from './utils';
import useLocalTaskError from '@dyl/pages/Tasks/edit/useLocalTaskError';

const useAnswerStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'row',

        '&:not(:hover) $arrowsGroup': {
            opacity: 0,
        }
    },
    field: {
        flex: 1,
    },
    arrowsGroup: {
        width: 48,
        height: 56,

        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',

        opacity: 1,
        transitionProperty: 'opacity',
        transitionDuration: '200ms',
    },
    arrow: {
        flex: 1,
    },
}));

export default function Answer({ index }) {
    const classes = useAnswerStyles();

    const isMicro = useMicroCheck();
    const [answer, setAnswer, apiError] = useTaskField('items', index, 'answers', 0, 'text');
    const [displayOrder] = useTaskField('items', index, 'answers', 0, 'display_order');
    const onAnswerChange = useChangeEvent(setAnswer);

    const [, setItems] = useTaskField('items');

    const punctuationError = (
        /[.!?,:;]\s*$/.test(answer ?? '')
            ? 'Answer should not have ending punctuation - a full stop will be applied automatically.'
            : null
    );

    useLocalTaskError(punctuationError && (
        // E.g. "Error in answer 1-B"
        `Error in answer ${Math.floor(index / 4) + 1}-${indexToLetter(index % 4)}.`
    ));

    const error = punctuationError || apiError;

    const move = useCallback(offset => {
        if (offset === 0) return;
        else if (offset > 0) offset++;
        setItems(prev => _(prev)
            .chunk(4)
            .map((section, sectionIndex) => {
                if (sectionIndex !== Math.floor(index / 4)) {
                    return section;
                }

                const subIndex = index - sectionIndex * 4;

                const orderedIndexes = deriveDisplayOrder(section);
                const thisOrder = orderedIndexes.indexOf(subIndex);

                orderedIndexes[thisOrder] = null;
                orderedIndexes.splice(thisOrder + offset, 0, subIndex);
                remove(orderedIndexes, isNil);

                return section.map((item, index) => ({
                    ...item,
                    answers: item.answers.map(answer => ({
                        ...answer,
                        display_order: orderedIndexes.indexOf(index) + 1,
                    })),
                }));
            })
            .flatten()
            .value()
        );
    }, [setItems, index]);

    const dragHandleClass = `answerDragHandle-${index}`;

    /** @type {React.MutableRefObject<HTMLDivElement} */
    const containerRef = useRef();
    const { height: containerHeight } = useSize(containerRef);

    const bounds = useMemo(() => ({
        top: - (displayOrder - 1) * containerHeight,
        bottom: (4 - displayOrder) * containerHeight,
        left: 0,
        right: 0,
    }), [containerHeight, displayOrder]);

    return (
        <Draggable
            disabled={isMicro}
            handle={`.${dragHandleClass}`}
            position={{ x: 0, y: 0 }}
            axis='y'
            bounds={bounds}
            onStop={(e, data) => {
                const rect = containerRef.current.getBoundingClientRect();
                const delta = data.lastY;

                const indexOffset = Math.round(delta / rect.height);

                if (indexOffset !== 0) {
                    move(indexOffset);
                }
            }}
        >
            <div className={classes.root} ref={containerRef}>
                <TextField
                    label={(index % 4 === 3)
                        ? 'Distractor'
                        : `Answer ${indexToLetter(index % 4)}${index === 0 ? ' (Example)' : ''}`
                    }
                    value={answer}
                    onChange={onAnswerChange}
                    variant='filled'
                    error={!!error}
                    helperText={error || ' '}
                    InputProps={{
                        startAdornment: !isMicro ? <Node lineId={index} side='end' /> : undefined,
                        endAdornment: !isMicro ? (
                            <InputAdornment position='end' style={{ cursor: 'grab ' }}>
                                <DragHandle className={dragHandleClass} />
                            </InputAdornment>
                        ) : undefined,
                    }}
                    className={classes.field}
                />

                {!isMicro && (
                    <div className={classes.arrowsGroup}>
                        <Button
                            className={classes.arrow}
                            onClick={() => move(-1)}
                            disabled={displayOrder === 1}
                            tabIndex={-1}
                        >
                            <KeyboardArrowUp fontSize='small' />
                        </Button>
                        <Button
                            className={classes.arrow}
                            onClick={() => move(1)}
                            disabled={displayOrder === 4}
                            tabIndex={-1}
                        >
                            <KeyboardArrowDown fontSize='small' />
                        </Button>
                    </div>
                )}
            </div>
        </Draggable>
    );
}